import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Slide,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import CheckIcon from '@mui/icons-material/CheckRounded'
import FlagIcon from '@mui/icons-material/FlagRounded'
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlagRounded'
import RadioIcon from '@mui/icons-material/RadioRounded'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import BlockIcon from '@mui/icons-material/BlockRounded'
import LinkIcon from '@mui/icons-material/LinkRounded'
import MergeIcon from '@mui/icons-material/MergeTypeRounded'
import SendIcon from '@mui/icons-material/SendRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendarRounded'
import QuickRepliesMenu from './QuickRepliesMenu'
import MessageBubble from './MessageBubble'
import type { ConversationMeta, Message } from '../../api/messages'
import * as messagesApi from '../../api/messages'
import useMessagesStore from '../../store/messages'
import useThreadsStore from '../../store/threads'
import useStationGeneralSettingsStore from '../../store/stationGeneralSettings'
import { createVisibilityObserver, isElementMostlyVisible } from '../../utils/visibilityObserver'
import {
  hasFlagged,
  hasOnAir,
  latestFlaggedMessageId,
  latestOnAirMessageId,
} from '../../utils/inbox/threadMarks'
import { ensureCallable, warnInbox } from '../../utils/inbox/safety'
import useContactsDetailStore from '../../store/contactsDetail'

const BottomCenter = (props: TransitionProps) => <Slide {...props} direction="up" />

interface ConversationProps {
  messages: Message[]
  meta: ConversationMeta | null
  activeMessage: Message | null
  loading: boolean
  sending: boolean
  markOnOpen: boolean
  disabled?: boolean
  onSendReply: (
    text: string,
    options?: { source?: 'composer' | 'quick-reply' },
  ) => Promise<{ tempId: number; finalize: Promise<number | null> } | null>
  onMarkReadToggle: (message: Message, next: boolean) => Promise<void>
  onFlagToggle: (message: Message, next: boolean) => Promise<void>
  onOnAirToggle: (message: Message, next: boolean) => Promise<void>
  onBlockIdentity: () => void
  onBlockContact: () => void
  onUnblock: () => void
  onLinkIdentity: () => void
  onMergeContact: () => void
  onClearThreadOnAir?: () => void | Promise<void>
  onClearThreadFlags?: () => void | Promise<void>
  onJumpToOnAir?: () => void
  onJumpToFlagged?: () => void
  error?: string | null
  onClose?: () => void
  firstUnreadId?: number | null
  requestId?: string | null
  onAutoMarkedRead?: (payload: {
    upToId: number
    contactId: number
    newlyReadIds: number[]
    unreadRemaining: number
  }) => void
  onNotify?: (message: string, severity?: 'success' | 'error') => void
  onOpenContactDetail: () => void
  contactDrawerOpen?: boolean
  contactButtonRef?: React.RefObject<HTMLButtonElement>
}

const Conversation: React.FC<ConversationProps> = ({
  messages,
  meta,
  activeMessage,
  loading,
  sending,
  markOnOpen,
  disabled = false,
  onSendReply,
  onMarkReadToggle,
  onFlagToggle,
  onOnAirToggle,
  onBlockIdentity,
  onBlockContact,
  onUnblock,
  onLinkIdentity,
  onMergeContact,
  onClearThreadOnAir,
  onClearThreadFlags,
  onJumpToOnAir,
  onJumpToFlagged,
  error,
  onClose,
  firstUnreadId = null,
  requestId = null,
  onAutoMarkedRead,
  onNotify,
  onOpenContactDetail,
  contactDrawerOpen = false,
  contactButtonRef,
}) => {
  const [replyText, setReplyText] = React.useState('')
  const [sendingState, setSendingState] = React.useState(false)
  const [initialMarking, setInitialMarking] = React.useState(false)
  const composerRef = React.useRef<HTMLTextAreaElement | null>(null)
  const composerContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollElRef = React.useRef<HTMLDivElement | null>(null)
  const userNearBottomRef = React.useRef(true)
  const [isNearBottom, setIsNearBottom] = React.useState(true)
  const prevCountRef = React.useRef<number>(0)
  const hasInitializedThreadRef = React.useRef(false)
  const [showNewPill, setShowNewPill] = React.useState(false)
  const [onAirMenuAnchorEl, setOnAirMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const [flagMenuAnchorEl, setFlagMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const previousFirstUnreadRef = React.useRef<number | null>(null)
  const armedRef = React.useRef(false)
  const userScrolledRef = React.useRef(false)
  const ioRef = React.useRef<ReturnType<typeof createVisibilityObserver> | null>(null)
  const ioActiveRef = React.useRef(false)

  const latestSeenIdRef = React.useRef<number | null>(null)
  const inflightRef = React.useRef(false)
  const flushTimerRef = React.useRef<number | null>(null)
  const runFlushRef = React.useRef<() => Promise<void>>(async () => {})
  const pendingObservationRef = React.useRef(new Map<number, HTMLElement>())
  const programmaticScrollRef = React.useRef(false)
  const initialMarkHandledRef = React.useRef<number | null>(null)
  const initialScrollDoneRef = React.useRef(false)
  const initialAutoMarkDoneRef = React.useRef(false)
  const initialVisibleSweepDoneRef = React.useRef(false)
  const scrollListenerCleanupRef = React.useRef<(() => void) | null>(null)
  const readOverridesRef = React.useRef<Set<number>>(new Set())
  const effectiveMessagesRef = React.useRef<Message[]>(messages)
  const jumpFlashTimersRef = React.useRef<Map<number, number>>(new Map())
  const pendingJumpLoadRef = React.useRef<number | null>(null)
  const blockedContact = Boolean(meta?.blocked.contact)
  const blockedIdentity = Boolean(meta?.blocked.identity)
  const isBlocked = blockedContact || blockedIdentity
  const firstMessageContactId = messages[0]?.contact_id ?? null
  const lastMessageContactId = messages.length > 0 ? messages[messages.length - 1]?.contact_id ?? null : null

  const derivedContactId = React.useMemo(() => {
    const candidates: Array<number | null | undefined> = [
      meta?.contact.id ?? null,
      activeMessage?.contact_id ?? null,
      lastMessageContactId,
      firstMessageContactId,
    ]

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
        return candidate
      }
    }

    return null
  }, [meta?.contact?.id, activeMessage?.contact_id, lastMessageContactId, firstMessageContactId])
  const activeThread = useMessagesStore(
    React.useCallback(
      (state) => (derivedContactId ? state.byContact[derivedContactId] : undefined),
      [derivedContactId],
    ),
  )
  const activeContactId = derivedContactId ?? activeThread?.contactId ?? null
  const activeThreadSummary = useThreadsStore(
    React.useCallback(
      (state) =>
        activeContactId
          ? state.items.find((item) => item.contact_id === activeContactId) ?? null
          : null,
      [activeContactId],
    ),
  )
  const activeContactDetail = useContactsDetailStore(
    React.useCallback(
      (state) => (activeContactId ? state.byId[activeContactId]?.data ?? null : null),
      [activeContactId],
    ),
  )
  const stationIdForSettings = React.useMemo(() => {
    if (activeMessage?.station_id && Number.isFinite(activeMessage.station_id)) {
      return activeMessage.station_id
    }

    const first = messages[0]?.station_id
    if (typeof first === 'number' && Number.isFinite(first)) {
      return first
    }

    return null
  }, [activeMessage?.station_id, messages])
  const autoSendQuickReplies = useStationGeneralSettingsStore(
    React.useCallback((state) => {
      if (!stationIdForSettings) {
        return false
      }

      return state.generalSettings[stationIdForSettings]?.autoSendQuickReplies ?? false
    }, [stationIdForSettings]),
  )
  const enterToSend = useStationGeneralSettingsStore(
    React.useCallback((state) => {
      if (!stationIdForSettings) {
        return false
      }

      return state.generalSettings[stationIdForSettings]?.enterToSend ?? false
    }, [stationIdForSettings]),
  )
  const markMessagesReadUpTo = useMessagesStore((state) => state.markReadUpTo)
  const markSingleMessageRead = useMessagesStore((state) => state.markReadSingle)
  const loadThread = useMessagesStore((state) => state.loadThread)
  const activeStationId = useMessagesStore((state) => state.activeStationId)
  const resolvedStationId = activeStationId ?? stationIdForSettings ?? null
  const recomputeThreadUnread = useThreadsStore((state) => state.recomputeUnread)
  const jumpTarget = useMessagesStore((state) => state.jumpTarget)
  const consumeJumpTarget = useMessagesStore((state) => state.consumeJumpTarget)
  const [readOverrides, setReadOverrides] = React.useState<Set<number>>(() => new Set())
  const [isComposingIME, setIsComposingIME] = React.useState(false)
  const effectiveMessages = React.useMemo(() => {
    if (readOverrides.size === 0) {
      return messages
    }

    return messages.map((item) => (readOverrides.has(item.id) ? { ...item, is_read: true } : item))
  }, [messages, readOverrides])
  const scrollToMessage = React.useCallback((messageId: number | null, options?: { highlight?: boolean }) => {
    const container = scrollElRef.current ?? scrollContainerRef.current

    if (!container) {
      return
    }

    const performScroll = () => {
      if (messageId) {
        const target = container.querySelector<HTMLElement>(`[data-msgid="${messageId}"]`)

        if (target) {
          target.scrollIntoView({ block: 'center' })
          if (options?.highlight && typeof window !== 'undefined') {
            const timers = jumpFlashTimersRef.current
            const existingTimer = timers.get(messageId)
            if (existingTimer) {
              window.clearTimeout(existingTimer)
              timers.delete(messageId)
            }
            target.classList.remove('MessageBubble--jumpFlash')
            void target.getBoundingClientRect()
            target.classList.add('MessageBubble--jumpFlash')
            const timeout = window.setTimeout(() => {
              target.classList.remove('MessageBubble--jumpFlash')
              timers.delete(messageId)
            }, 900)
            timers.set(messageId, timeout)
          }
          previousFirstUnreadRef.current = messageId
          return
        }
      }

      container.scrollTop = container.scrollHeight
      previousFirstUnreadRef.current = null
    }

    const complete = () => {
      programmaticScrollRef.current = false
    }

    const run = () => {
      performScroll()
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(complete)
      } else {
        complete()
      }
    }

    programmaticScrollRef.current = true

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(run)
    } else {
      run()
    }
  }, [])
  React.useEffect(() => {
    effectiveMessagesRef.current = effectiveMessages
  }, [effectiveMessages])
  React.useEffect(() => {
    if (!jumpTarget) {
      pendingJumpLoadRef.current = null
      return
    }

    if (!activeContactId || jumpTarget.contactId !== activeContactId) {
      return
    }

    const hasMessage = effectiveMessages.some((item) => item.id === jumpTarget.messageId)

    if (!hasMessage) {
      if (
        resolvedStationId &&
        !activeThread?.loading &&
        pendingJumpLoadRef.current !== jumpTarget.messageId
      ) {
        pendingJumpLoadRef.current = jumpTarget.messageId
        void loadThread(activeContactId, resolvedStationId, { force: true })
      }
      return
    }

    const messageId = Number(jumpTarget.messageId)

    if (!Number.isFinite(messageId) || messageId <= 0) {
      warnInbox('[Inbox] Invalid jump target message id', {
        contactId: jumpTarget.contactId,
        messageId: jumpTarget.messageId,
      })
      consumeJumpTarget(jumpTarget.contactId, jumpTarget.messageId)
      pendingJumpLoadRef.current = null
      return
    }

    scrollToMessage(messageId, { highlight: jumpTarget.highlight !== false })
    consumeJumpTarget(jumpTarget.contactId, messageId)
    pendingJumpLoadRef.current = null
  }, [
    jumpTarget,
    activeContactId,
    effectiveMessages,
    scrollToMessage,
    consumeJumpTarget,
    loadThread,
    resolvedStationId,
    activeThread?.loading,
  ])
  React.useEffect(() => {
    pendingJumpLoadRef.current = null
  }, [activeContactId])
  React.useEffect(() => {
    return () => {
      const timers = jumpFlashTimersRef.current
      if (typeof window !== 'undefined') {
        timers.forEach((timer) => {
          window.clearTimeout(timer)
        })
      }
      timers.clear()
    }
  }, [])
  React.useEffect(() => {
    previousFirstUnreadRef.current = null
    initialScrollDoneRef.current = false
    initialAutoMarkDoneRef.current = false
    initialMarkHandledRef.current = null
    latestSeenIdRef.current = null
    pendingObservationRef.current.clear()
    readOverridesRef.current = new Set()
    setReadOverrides(new Set())
    armedRef.current = false
    userScrolledRef.current = false
  }, [meta?.contact.id, requestId])
  const NEAR_BOTTOM_PX = 120
  const isNearBottomFn = React.useCallback((el: HTMLElement, threshold = NEAR_BOTTOM_PX) => {
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
  }, [])
  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollElRef.current
    if (!el) {
      return
    }

    el.scrollTo({ top: el.scrollHeight, behavior })
  }, [])
  const scrollToMessageId = React.useCallback(
    (id: number, behavior: ScrollBehavior = 'smooth') => {
      const el = scrollElRef.current ?? scrollContainerRef.current
      if (!el) {
        return
      }

      const target = el.querySelector<HTMLElement>(`.rpa-msg-bubble[data-msgid="${id}"]`)
      target?.scrollIntoView({ block: 'nearest', behavior })
    },
    [],
  )
  const disconnectObserver = React.useCallback((clearEntries = false) => {
    if (ioRef.current) {
      ioRef.current.disconnect()
      ioRef.current = null
    }
    ioActiveRef.current = false

    if (clearEntries) {
      pendingObservationRef.current.clear()
    }
  }, [])
  const registerVisibility = React.useCallback(
    (element: HTMLElement, id: number) => {
      if (!markOnOpen || !meta?.contact.id) {
        return
      }

      const message = effectiveMessagesRef.current.find((item) => item.id === id)

      if (!message || message.contact_id !== meta.contact.id || message.is_read) {
        pendingObservationRef.current.delete(id)
        return
      }

      pendingObservationRef.current.set(id, element)
      ;(element as unknown as { __rpa_payload?: { id: number } }).__rpa_payload = { id }

      if (!ioRef.current || !ioActiveRef.current) {
        return
      }

      ioRef.current.observe(element)

      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[conversation] observed bubble', {
          id,
          contactId: meta.contact.id,
          requestId,
        })
      }
    },
    [markOnOpen, meta?.contact.id, requestId],
  )
  const runFlush = React.useCallback(async () => {
    if (inflightRef.current) {
      return
    }

    if (!markOnOpen || !meta?.contact.id) {
      latestSeenIdRef.current = null
      return
    }

    const contactId = meta.contact.id
    const upToId = latestSeenIdRef.current

    if (!upToId) {
      return
    }

    const currentMessages = effectiveMessagesRef.current
    const stationId = currentMessages[0]?.station_id ?? null

    if (!stationId) {
      latestSeenIdRef.current = null
      return
    }

    const unreadMessages = currentMessages.filter(
      (item) => !item.is_read && item.contact_id === contactId && item.id <= upToId,
    )

    if (!unreadMessages.length) {
      latestSeenIdRef.current = null
      return
    }

    const ids = unreadMessages.map((item) => item.id)

    inflightRef.current = true

    setReadOverrides((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      readOverridesRef.current = next
      return next
    })

    markMessagesReadUpTo(contactId, upToId)
    recomputeThreadUnread(contactId)

    const overrides = new Set(readOverridesRef.current)
    ids.forEach((id) => overrides.add(id))
    readOverridesRef.current = overrides

    const unreadRemaining = currentMessages.reduce((total, item) => {
      if (item.contact_id !== contactId) {
        return total
      }

      const isReadAfter = overrides.has(item.id) || item.id <= upToId || item.is_read
      return isReadAfter ? total : total + 1
    }, 0)

    onAutoMarkedRead?.({ upToId, contactId, newlyReadIds: ids, unreadRemaining })

    try {
      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[conversation] bulk flush upToId', {
          contactId,
          upToId,
          ids,
          requestId,
        })
      }
      await messagesApi.markReadBulk(stationId, contactId, upToId)
      ids.forEach((id) => pendingObservationRef.current.delete(id))
    } catch (error) {
      console.warn('[conversation] bulk mark failed', error)
    } finally {
      const pending = latestSeenIdRef.current
      inflightRef.current = false
      latestSeenIdRef.current = null

      if (pending && pending > upToId) {
        latestSeenIdRef.current = pending
        if (!flushTimerRef.current && typeof window !== 'undefined') {
          flushTimerRef.current = window.setTimeout(() => {
            flushTimerRef.current = null
            void runFlushRef.current()
          }, 200)
        }
      }
    }
  }, [
    markMessagesReadUpTo,
    markOnOpen,
    meta?.contact.id,
    onAutoMarkedRead,
    recomputeThreadUnread,
    requestId,
  ])
  const scheduleFlush = React.useCallback(() => {
    if (flushTimerRef.current || typeof window === 'undefined') {
      return
    }

    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null
      void runFlushRef.current()
    }, 200)
  }, [])
  const attachScrollListener = React.useCallback(() => {
    if (!markOnOpen) {
      return
    }

    const scrollEl = scrollElRef.current ?? scrollContainerRef.current

    if (!scrollEl) {
      return
    }

    if (scrollListenerCleanupRef.current) {
      scrollListenerCleanupRef.current()
    }

    const arm = () => {
      if (programmaticScrollRef.current) {
        return
      }

      if (!armedRef.current) {
        armedRef.current = true
        if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
          window.console.log('[conversation] armed', {
            contactId: meta?.contact.id ?? null,
            requestId,
          })
        }
      }
    }

    const onScroll = () => {
      if (programmaticScrollRef.current) {
        return
      }

      if (!userScrolledRef.current) {
        userScrolledRef.current = true
      }

      arm()
    }

    const options = { passive: true } as const

    scrollEl.addEventListener('scroll', onScroll, options)
    scrollEl.addEventListener('wheel', arm, options)
    scrollEl.addEventListener('touchmove', arm, options)
    window.addEventListener('keydown', arm, options)

    scrollListenerCleanupRef.current = () => {
      scrollEl.removeEventListener('scroll', onScroll)
      scrollEl.removeEventListener('wheel', arm)
      scrollEl.removeEventListener('touchmove', arm)
      window.removeEventListener('keydown', arm)
    }
  }, [markOnOpen, meta?.contact.id, requestId])
  React.useEffect(() => {
    runFlushRef.current = runFlush
  }, [runFlush])

  React.useEffect(() => {
    previousFirstUnreadRef.current = null
  }, [meta?.contact.id])

  React.useEffect(() => {
    const reset = new Set<number>()
    setReadOverrides(reset)
    readOverridesRef.current = reset
    latestSeenIdRef.current = null
    initialMarkHandledRef.current = null
    armedRef.current = false
    userScrolledRef.current = false
    programmaticScrollRef.current = false
    setInitialMarking(false)
    initialScrollDoneRef.current = false
    initialAutoMarkDoneRef.current = false
    initialVisibleSweepDoneRef.current = false
    userNearBottomRef.current = true
    setIsNearBottom(true)
    setShowNewPill(false)
    prevCountRef.current = effectiveMessagesRef.current.length
    hasInitializedThreadRef.current = false
  }, [meta?.contact.id, requestId])

  React.useEffect(() => {
    readOverridesRef.current = readOverrides
  }, [readOverrides])


  React.useEffect(() => {
    const contactId = activeContactId

    const el =
      typeof document !== 'undefined'
        ? (document.querySelector('.rpa-thread-scroll') as HTMLDivElement | null)
        : scrollContainerRef.current

    scrollElRef.current = el

    const onScroll = () => {
      if (!el) {
        return
      }
      const near = isNearBottomFn(el)
      userNearBottomRef.current = near
      setIsNearBottom(near)
      if (near) {
        setShowNewPill(false)
      }
    }

    el?.addEventListener('scroll', onScroll, { passive: true })

    const init = () => {
      if (!el) {
        return
      }
      const near = isNearBottomFn(el)
      userNearBottomRef.current = near
      setIsNearBottom(near)
      if (near) {
        setShowNewPill(false)
      }
    }

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        init()
      })
    } else {
      init()
    }

    if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
      window.console.log('[conversation] onThreadChange', {
        contactId,
        requestId,
      })
    }

    if (flushTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(flushTimerRef.current)
      flushTimerRef.current = null
    }

    armedRef.current = false
    userScrolledRef.current = false
    latestSeenIdRef.current = null
    inflightRef.current = false
    programmaticScrollRef.current = false
    pendingObservationRef.current.clear()

    if (scrollListenerCleanupRef.current) {
      scrollListenerCleanupRef.current()
      scrollListenerCleanupRef.current = null
    }

    disconnectObserver(true)

    if (contactId && markOnOpen) {
      attachScrollListener()
    }

    return () => {
      el?.removeEventListener('scroll', onScroll)
    }
  }, [
    activeContactId,
    attachScrollListener,
    disconnectObserver,
    isNearBottomFn,
    markOnOpen,
    requestId,
  ])

  React.useEffect(() => {
    if (readOverrides.size === 0) {
      return
    }

    setReadOverrides((prev) => {
      if (prev.size === 0) {
        return prev
      }
      const next = new Set(prev)
      let changed = false

      prev.forEach((id) => {
        const exists = messages.find((item) => item.id === id)
        if (!exists || exists.is_read) {
          next.delete(id)
          changed = true
        }
      })

      if (!changed) {
        return prev
      }

      const updated = new Set(next)
      readOverridesRef.current = updated
      return updated
    })
  }, [messages, readOverrides.size])

  React.useEffect(() => {
    if (!markOnOpen) {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current)
        flushTimerRef.current = null
      }
      latestSeenIdRef.current = null
      armedRef.current = false
      userScrolledRef.current = false
      if (scrollListenerCleanupRef.current) {
        scrollListenerCleanupRef.current()
        scrollListenerCleanupRef.current = null
      }
      disconnectObserver(true)
      setInitialMarking(false)
      initialAutoMarkDoneRef.current = false
      initialVisibleSweepDoneRef.current = false
    }
  }, [disconnectObserver, markOnOpen])

  React.useEffect(() => {
    const container = scrollElRef.current ?? scrollContainerRef.current

    if (!markOnOpen || !container || !meta?.contact.id) {
      disconnectObserver()
      return
    }

    disconnectObserver()

    const observer = createVisibilityObserver({
      root: container,
      threshold: 0.75,
      rootMargin: '0px 0px -10%',
      onSeen: ({ id }) => {
        if (!armedRef.current) {
          return
        }

        latestSeenIdRef.current =
          latestSeenIdRef.current === null ? id : Math.max(latestSeenIdRef.current, id)

        if (inflightRef.current) {
          return
        }

        if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
          window.console.log('[conversation] onSeen', { contactId: meta.contact.id, id, requestId })
        }

        scheduleFlush()
      },
    })

    ioRef.current = observer
    ioActiveRef.current = true

    pendingObservationRef.current.forEach((element, messageId) => {
      if (!ioRef.current || !ioActiveRef.current || !element) {
        return
      }

      const message = effectiveMessagesRef.current.find((item) => item.id === messageId)

      if (!message || message.contact_id !== meta.contact.id) {
        pendingObservationRef.current.delete(messageId)
        return
      }

      if (message.is_read) {
        pendingObservationRef.current.delete(messageId)
        return
      }

      ;(element as unknown as { __rpa_payload?: { id: number } }).__rpa_payload = {
        id: messageId,
      }
      ioRef.current.observe(element)

      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[conversation] observed bubble', {
          id: messageId,
          contactId: meta.contact.id,
          requestId,
        })
      }
    })

    if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
      window.console.log('[conversation] io created', {
        contactId: meta.contact.id,
        requestId,
      })
    }

    return () => {
      observer.disconnect()
      if (ioRef.current === observer) {
        ioRef.current = null
      }
      ioActiveRef.current = false
    }
  }, [disconnectObserver, markOnOpen, meta?.contact.id, requestId, scheduleFlush])

  React.useEffect(() => {
    if (!markOnOpen || !meta || initialMarking) {
      return
    }

    if (!scrollListenerCleanupRef.current) {
      attachScrollListener()
    }
  }, [attachScrollListener, initialMarking, markOnOpen, meta?.contact.id])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const globalWindow = window as unknown as {
      __RPA_DEBUG?: Record<string, unknown>
    }

    globalWindow.__RPA_DEBUG = {
      ...(globalWindow.__RPA_DEBUG ?? {}),
      conversation: {
        armed: armedRef.current,
        observed: ioRef.current ? ioRef.current.observedSet : undefined,
        observerActive: ioActiveRef.current,
        latestSeenId: latestSeenIdRef.current,
      },
    }
  })

  React.useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current)
      }
      latestSeenIdRef.current = null
      if (scrollListenerCleanupRef.current) {
        scrollListenerCleanupRef.current()
        scrollListenerCleanupRef.current = null
      }
      disconnectObserver(true)
    }
  }, [disconnectObserver])

  const focusComposer = React.useCallback((value: string) => {
    const focus = () => {
      if (composerRef.current) {
        const length = value.length
        composerRef.current.focus()
        composerRef.current.setSelectionRange(length, length)
      }
    }

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(focus)
    } else {
      focus()
    }
  }, [])

  React.useEffect(() => {
    if (!effectiveMessages.length || initialScrollDoneRef.current) {
      return
    }

    if (firstUnreadId) {
      scrollToMessage(firstUnreadId)
      initialScrollDoneRef.current = true
      return
    }

    scrollToMessage(null)
    initialScrollDoneRef.current = true
  }, [effectiveMessages, firstUnreadId, scrollToMessage, requestId])

  React.useEffect(() => {
    if (!markOnOpen || !meta) {
      return
    }

    if (initialAutoMarkDoneRef.current) {
      return
    }

    if (!firstUnreadId) {
      initialMarkHandledRef.current = null
      return
    }

    const targetMessage = effectiveMessages.find(
      (item) => item.id === firstUnreadId && item.contact_id === meta.contact.id,
    )

    if (!targetMessage) {
      initialMarkHandledRef.current = null
      return
    }

    if (targetMessage.is_read) {
      initialMarkHandledRef.current = targetMessage.id
      initialAutoMarkDoneRef.current = true
      return
    }

    if (initialMarkHandledRef.current === targetMessage.id) {
      return
    }

    let cancelled = false

    initialMarkHandledRef.current = targetMessage.id
    initialAutoMarkDoneRef.current = true
    armedRef.current = false
    userScrolledRef.current = false

    const begin = () => {
      if (cancelled) {
        return
      }

      if (scrollListenerCleanupRef.current) {
        scrollListenerCleanupRef.current()
        scrollListenerCleanupRef.current = null
      }
      disconnectObserver()

      setInitialMarking(true)

      setReadOverrides((prev) => {
        const next = new Set(prev)
        next.add(targetMessage.id)
        readOverridesRef.current = next
        return next
      })

      pendingObservationRef.current.delete(targetMessage.id)

      scrollToMessage(targetMessage.id)
      initialScrollDoneRef.current = true

      markSingleMessageRead(targetMessage.contact_id, targetMessage.id)
      recomputeThreadUnread(targetMessage.contact_id)

      const overrides = new Set(readOverridesRef.current)
      overrides.add(targetMessage.id)
      readOverridesRef.current = overrides

      const unreadRemaining = effectiveMessagesRef.current.reduce((total, item) => {
        if (item.contact_id !== targetMessage.contact_id) {
          return total
        }

        const isReadAfter = overrides.has(item.id) || item.is_read || item.id === targetMessage.id
        return isReadAfter ? total : total + 1
      }, 0)

      onAutoMarkedRead?.({
        upToId: targetMessage.id,
        contactId: targetMessage.contact_id,
        newlyReadIds: [targetMessage.id],
        unreadRemaining,
      })

      const run = async () => {
        try {
          await messagesApi.markRead(targetMessage.id, targetMessage.station_id, true)
        } catch (error) {
          if (!cancelled) {
            setReadOverrides((prev) => {
              const next = new Set(prev)
              next.delete(targetMessage.id)
              return next
            })

            const filtered = new Set(readOverridesRef.current)
            filtered.delete(targetMessage.id)
            readOverridesRef.current = filtered

            initialMarkHandledRef.current = null
            initialAutoMarkDoneRef.current = false
            console.warn('Failed to mark first unread message as read', error)
            onNotify?.("Couldn't mark as read", 'error')

            void (async () => {
              try {
                await loadThread(targetMessage.contact_id, targetMessage.station_id, { force: true })
              } catch (loadError) {
                console.warn('Failed to reload thread after single mark failure', loadError)
              } finally {
                recomputeThreadUnread(targetMessage.contact_id)
              }
            })()
          }
        } finally {
          if (!cancelled) {
            setInitialMarking(false)
            attachScrollListener()
          }
        }
      }

      void run()
    }

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(begin)
    } else {
      begin()
    }

    return () => {
      cancelled = true
      setInitialMarking(false)
    }
  }, [
    attachScrollListener,
    disconnectObserver,
    effectiveMessages,
    firstUnreadId,
    loadThread,
    markSingleMessageRead,
    markOnOpen,
    meta?.contact.id,
    onAutoMarkedRead,
    onNotify,
    recomputeThreadUnread,
    requestId,
    scrollToMessage,
  ])

  React.useEffect(() => {
    if (!markOnOpen || !meta?.contact.id) {
      return
    }

    if (initialVisibleSweepDoneRef.current) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    const frame = window.requestAnimationFrame(() => {
      if (initialVisibleSweepDoneRef.current) {
        return
      }

      initialVisibleSweepDoneRef.current = true

      const root = scrollElRef.current ?? scrollContainerRef.current ?? null
      const unreadElements = Array.from(
        document.querySelectorAll<HTMLElement>('.rpa-msg-bubble.unread'),
      )

      unreadElements.forEach((element) => {
        const attr = element.getAttribute('data-msgid')
        const id = attr ? Number(attr) : NaN

        if (!Number.isFinite(id)) {
          return
        }

        if (isElementMostlyVisible(element, root, 0.75)) {
          latestSeenIdRef.current = Math.max(latestSeenIdRef.current ?? 0, id)
        }
      })

      if (latestSeenIdRef.current) {
        scheduleFlush()
      }
    })

    return () => {
      if (typeof window !== 'undefined') {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [markOnOpen, meta?.contact.id, requestId, scheduleFlush, effectiveMessages])

  const submitReply = React.useCallback(async () => {
    const value = replyText.trim()

    if (!value || sendingState || sending) {
      return
    }

    setSendingState(true)

    try {
      const result = await onSendReply(value, { source: 'composer' })

      if (!result) {
        return
      }

      const { tempId, finalize } = result

      if (Number.isFinite(tempId)) {
        setShowNewPill(false)
        const run = () => scrollToMessageId(tempId, 'smooth')
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(run)
        } else {
          run()
        }
      }

      let finalId: number | null = null
      try {
        finalId = await finalize
      } catch (error) {
        throw error
      }

      setReplyText('')

      if (userNearBottomRef.current) {
        const run = () => {
          if (typeof finalId === 'number' && Number.isFinite(finalId)) {
            scrollToMessageId(finalId, 'auto')
          } else {
            scrollToBottom('auto')
          }
        }
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(run)
        } else {
          run()
        }
      }
    } finally {
      setSendingState(false)
    }
  }, [
    replyText,
    sendingState,
    sending,
    onSendReply,
    scrollToMessageId,
    scrollToBottom,
  ])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await submitReply()
  }

  const sendQuickReply = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim()

      if (!trimmed || sendingState || sending) {
        return
      }

      setSendingState(true)

      try {
        const result = await onSendReply(trimmed, { source: 'quick-reply' })

        if (!result) {
          return
        }

        const { tempId, finalize } = result

        if (Number.isFinite(tempId)) {
          setShowNewPill(false)
          const run = () => scrollToMessageId(tempId, 'smooth')
          if (typeof window !== 'undefined') {
            window.requestAnimationFrame(run)
          } else {
            run()
          }
        }

        let finalId: number | null = null

        try {
          finalId = await finalize
        } catch (error) {
          throw error
        }

        if (userNearBottomRef.current) {
          const run = () => {
            if (typeof finalId === 'number' && Number.isFinite(finalId)) {
              scrollToMessageId(finalId, 'auto')
            } else {
              scrollToBottom('auto')
            }
          }

          if (typeof window !== 'undefined') {
            window.requestAnimationFrame(run)
          } else {
            run()
          }
        }
      } finally {
        setSendingState(false)
      }
    },
    [
      onSendReply,
      scrollToBottom,
      scrollToMessageId,
      sending,
      sendingState,
      setShowNewPill,
    ],
  )

  const handleBubbleFlagToggle = React.useCallback(
    (message: Message, next: boolean) => {
      if (disabled || loading) {
        return
      }
      if (!ensureCallable(onFlagToggle, 'onFlagToggle', { messageId: message.id, next })) {
        return
      }
      void onFlagToggle(message, next)
    },
    [disabled, loading, onFlagToggle],
  )

  const handleBubbleOnAirToggle = React.useCallback(
    (message: Message, next: boolean) => {
      if (disabled || loading) {
        return
      }
      if (!ensureCallable(onOnAirToggle, 'onOnAirToggle', { messageId: message.id, next })) {
        return
      }
      void onOnAirToggle(message, next)
    },
    [disabled, loading, onOnAirToggle],
  )

  const handleQuickReply = React.useCallback(
    (text: string) => {
      if (autoSendQuickReplies) {
        sendQuickReply(text).catch(() => {
          // Errors are handled within the send pipeline and surfaced via notifications.
        })
        return
      }

      setReplyText((prev) => {
        const next = prev ? `${prev.trimEnd()} ${text}` : text
        focusComposer(next)
        return next
      })
    },
    [autoSendQuickReplies, focusComposer, sendQuickReply],
  )

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault()
      void submitReply()
      return
    }

    if (event.key !== 'Enter') {
      return
    }

    if (event.isComposing || isComposingIME) {
      return
    }

    if (event.shiftKey || event.altKey) {
      return
    }

    if (!enterToSend) {
      return
    }

    event.preventDefault()
    void submitReply()
  }

  const handleComposerCompositionStart = React.useCallback(() => {
    setIsComposingIME(true)
  }, [])

  const handleComposerCompositionEnd = React.useCallback(() => {
    setIsComposingIME(false)
  }, [])

  const latestMessage = effectiveMessages[effectiveMessages.length - 1] ?? null

  React.useEffect(() => {
    if (!activeContactId || !activeThread) {
      return
    }

    const count = activeThread.items.length
    const prev = prevCountRef.current
    prevCountRef.current = count

    if (!hasInitializedThreadRef.current) {
      hasInitializedThreadRef.current = true
      return
    }

    if (count <= prev) {
      return
    }

    const newest = activeThread.items[activeThread.items.length - 1]

    if (!newest || newest.direction === 'out') {
      return
    }

    if (!scrollElRef.current) {
      return
    }

    if (userNearBottomRef.current) {
      const run = () => scrollToBottom('smooth')
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(run)
      } else {
        run()
      }
      setShowNewPill(false)
    } else {
      setShowNewPill(true)
    }
  }, [activeContactId, activeThread?.items.length])
  const targetMessage = activeMessage
    ? effectiveMessages.find((item) => item.id === activeMessage.id) ?? latestMessage
    : latestMessage

  const pickFirstNonEmpty = (
    ...values: Array<string | null | undefined>
  ): string => {
    for (const value of values) {
      const trimmed = value?.trim()
      if (trimmed) {
        return trimmed
      }
    }
    return ''
  }

  const resolveIdentityDisplayName = (
    identities: Array<{ id: number; display_name?: string | null }> | undefined,
    identityId: number | null,
  ): string | undefined => {
    if (!identityId || !identities?.length) {
      return undefined
    }

    const match = identities.find((identity) => identity.id === identityId)
    return match?.display_name ?? undefined
  }

  const activeIdentityId =
    meta?.active_identity ?? activeMessage?.contact_identity_id ?? targetMessage?.contact_identity_id ?? null

  const identityDisplayName = pickFirstNonEmpty(
    resolveIdentityDisplayName(meta?.identities, activeIdentityId),
    resolveIdentityDisplayName(activeThread?.meta?.identities, activeIdentityId),
    resolveIdentityDisplayName(activeThreadSummary?.identities, activeIdentityId),
    meta?.identities?.[0]?.display_name,
    activeThread?.meta?.identities?.[0]?.display_name,
    activeThreadSummary?.identities?.[0]?.display_name,
    activeMessage?.identity?.display_name,
    targetMessage?.identity?.display_name,
  )

  const baseDisplayName = pickFirstNonEmpty(
    meta?.contact.name,
    activeThread?.meta?.contact?.name,
    activeContactDetail?.contact?.name ?? undefined,
    activeThreadSummary?.contact_name,
    activeMessage?.contact?.name,
    targetMessage?.contact?.name,
    identityDisplayName,
  )
  const hasConversationContext = Boolean(
    meta ||
      activeMessage ||
      targetMessage ||
      activeThreadSummary ||
      activeContactDetail ||
      activeThread?.meta,
  )
  const displayName = baseDisplayName || (hasConversationContext ? 'Unknown' : 'Select a message')
  const locationLabel = pickFirstNonEmpty(
    meta?.contact.location,
    activeThread?.meta?.contact?.location,
    activeContactDetail?.contact?.location ?? undefined,
    activeMessage?.contact?.location,
    targetMessage?.contact?.location,
  )
  const headerTitle = locationLabel ? `${displayName} — ${locationLabel}` : displayName
  const threadHasOnAir = hasOnAir(activeThreadSummary)
  const threadHasFlagged = hasFlagged(activeThreadSummary)
  const latestOnAirId = latestOnAirMessageId(activeThreadSummary)
  const latestFlaggedId = latestFlaggedMessageId(activeThreadSummary)
  const onAirMenuOpen = Boolean(onAirMenuAnchorEl)
  const flagMenuOpen = Boolean(flagMenuAnchorEl)
  const onAirButtonDisabled = disabled || (!threadHasOnAir && (!targetMessage || loading))
  const flagButtonDisabled = disabled || (!threadHasFlagged && (!targetMessage || loading))
  const markCurrentOnAirDisabled = disabled || loading || !targetMessage || targetMessage.is_on_air
  const markCurrentFlaggedDisabled = disabled || loading || !targetMessage || targetMessage.is_flagged
  const jumpOnAirDisabled = !threadHasOnAir || !latestOnAirId || !onJumpToOnAir
  const jumpFlaggedDisabled = !threadHasFlagged || !latestFlaggedId || !onJumpToFlagged
  const clearOnAirDisabled = !threadHasOnAir || !onClearThreadOnAir
  const clearFlaggedDisabled = !threadHasFlagged || !onClearThreadFlags
  const onAirMenuId = 'conversation-on-air-menu'
  const flagMenuId = 'conversation-flagged-menu'

  const handleOpenOnAirMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOnAirMenuAnchorEl(event.currentTarget)
  }

  const handleCloseOnAirMenu = () => {
    setOnAirMenuAnchorEl(null)
  }

  const handleOpenFlagMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFlagMenuAnchorEl(event.currentTarget)
  }

  const handleCloseFlagMenu = () => {
    setFlagMenuAnchorEl(null)
  }

  const runAsync = (label: string, fn?: () => void | Promise<void>) => {
    if (!ensureCallable(fn, label)) {
      return false
    }

    try {
      const result = fn()
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        void (result as Promise<unknown>).catch((error) => {
          warnInbox(`[Inbox] Async handler rejected: ${label}`, { error })
        })
      }
      return true
    } catch (error) {
      warnInbox(`[Inbox] Handler threw: ${label}`, { error })
      return false
    }
  }

  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="rpa-thread-panel ConversationPane"
      sx={{ height: '100%' }}
    >
      <Box className="ConversationPane__header">
        <Box className="ConvHeader" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {onClose ? (
            <div className="ConvHeader__mobileClose">
              <Tooltip title="Close conversation">
                <span>
                  <IconButton
                    onClick={onClose}
                    sx={{ display: { md: 'none' } }}
                    aria-label="Close conversation"
                  >
                    <CloseIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          ) : null}
          <div className="ConvHeader__left">
            <Typography
              variant="h6"
              component="h1"
              className="ConvHeader__title"
              title={headerTitle}
              sx={{ '& .ConvHeader__meta, & .ConvHeader__sep': { color: 'text.secondary' } }}
            >
              <span className="ConvHeader__name">{displayName}</span>
              {locationLabel ? (
                <>
                  <span className="ConvHeader__sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="ConvHeader__meta">{locationLabel}</span>
                </>
              ) : null}
            </Typography>
          </div>
          <div className="ConvHeader__actions">
            <Tooltip title="On-Air actions">
              <span>
                <IconButton
                  onClick={handleOpenOnAirMenu}
                  disabled={onAirButtonDisabled}
                  size="small"
                  aria-haspopup="menu"
                  aria-controls={onAirMenuOpen ? onAirMenuId : undefined}
                  aria-expanded={onAirMenuOpen ? 'true' : undefined}
                  aria-label="On-Air actions"
                >
                  <RadioIcon fontSize="small" color={threadHasOnAir ? 'secondary' : 'inherit'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Flagged actions">
              <span>
                <IconButton
                  onClick={handleOpenFlagMenu}
                  disabled={flagButtonDisabled}
                  size="small"
                  aria-haspopup="menu"
                  aria-controls={flagMenuOpen ? flagMenuId : undefined}
                  aria-expanded={flagMenuOpen ? 'true' : undefined}
                  aria-label="Flagged actions"
                >
                  <FlagIcon fontSize="small" color={threadHasFlagged ? 'warning' : 'inherit'} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View contact details">
              <span>
                <IconButton
                  onClick={onOpenContactDetail}
                  disabled={!meta || loading}
                  size="small"
                  aria-label="Open contact details"
                  aria-pressed={contactDrawerOpen ? 'true' : undefined}
                  ref={contactButtonRef}
                >
                  <PermContactCalendarIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Link identity to another contact">
              <span>
                <IconButton onClick={onLinkIdentity} disabled={!meta || loading} size="small" aria-label="Link identity">
                  <LinkIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Merge contacts">
              <span>
                <IconButton onClick={onMergeContact} disabled={!meta || loading} size="small" aria-label="Merge contacts">
                  <MergeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <Menu
            id={onAirMenuId}
            anchorEl={onAirMenuAnchorEl}
            open={onAirMenuOpen}
            onClose={handleCloseOnAirMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem
              disabled={markCurrentOnAirDisabled}
              onClick={() => {
                handleCloseOnAirMenu()
                if (targetMessage) {
                  handleBubbleOnAirToggle(targetMessage, true)
                }
              }}
            >
              <ListItemIcon>
                <RadioIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Mark current message On-Air" />
            </MenuItem>
            {threadHasOnAir && latestOnAirId ? (
              <MenuItem
                disabled={jumpOnAirDisabled}
                onClick={() => {
                  handleCloseOnAirMenu()
                  if (
                    ensureCallable(onJumpToOnAir, 'onJumpToOnAir', {
                      threadId: activeContactId,
                    })
                  ) {
                    onJumpToOnAir()
                  }
                }}
              >
                <ListItemIcon>
                  <RadioIcon fontSize="small" color="secondary" />
                </ListItemIcon>
                <ListItemText primary="Jump to On-Air message" />
              </MenuItem>
            ) : null}
            {threadHasOnAir ? (
              <MenuItem
                disabled={clearOnAirDisabled}
                onClick={() => {
                  handleCloseOnAirMenu()
                  runAsync('onClearThreadOnAir', onClearThreadOnAir)
                }}
              >
                <ListItemIcon>
                  <RadioButtonUncheckedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Clear On-Air (this thread)" />
              </MenuItem>
            ) : null}
          </Menu>
          <Menu
            id={flagMenuId}
            anchorEl={flagMenuAnchorEl}
            open={flagMenuOpen}
            onClose={handleCloseFlagMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem
              disabled={markCurrentFlaggedDisabled}
              onClick={() => {
                handleCloseFlagMenu()
                if (targetMessage) {
                  handleBubbleFlagToggle(targetMessage, true)
                }
              }}
            >
              <ListItemIcon>
                <FlagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Flag current message" />
            </MenuItem>
            {threadHasFlagged && latestFlaggedId ? (
              <MenuItem
                disabled={jumpFlaggedDisabled}
                onClick={() => {
                  handleCloseFlagMenu()
                  if (
                    ensureCallable(onJumpToFlagged, 'onJumpToFlagged', {
                      threadId: activeContactId,
                    })
                  ) {
                    onJumpToFlagged()
                  }
                }}
              >
                <ListItemIcon>
                  <FlagIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="Jump to flagged message" />
              </MenuItem>
            ) : null}
            {threadHasFlagged ? (
              <MenuItem
                disabled={clearFlaggedDisabled}
                onClick={() => {
                  handleCloseFlagMenu()
                  runAsync('onClearThreadFlags', onClearThreadFlags)
                }}
              >
                <ListItemIcon>
                  <OutlinedFlagIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Clear flags (this thread)" />
              </MenuItem>
            ) : null}
          </Menu>
        </Box>

        {initialMarking ? <LinearProgress color="primary" sx={{ height: 2 }} /> : null}

        {isBlocked && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onUnblock}>
                Unblock
              </Button>
            }
            sx={{ borderRadius: 0, py: 1 }}
          >
            Replies are disabled because this {blockedContact ? 'contact' : 'identity'} is blocked.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}
      </Box>

      <Snackbar
        open={showNewPill}
        onClose={() => setShowNewPill(false)}
        TransitionComponent={BottomCenter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="New messages"
        action={
          <Button
            size="small"
            onClick={() => {
              scrollToBottom('smooth')
              userNearBottomRef.current = true
              setIsNearBottom(true)
              setShowNewPill(false)
            }}
          >
            Jump ↓
          </Button>
        }
      />

      <Box
        ref={scrollContainerRef}
        className="rpa-thread-scroll rpa-thread-container rpa-conversation-scroll ConversationPane__scroll"
        sx={{ overflowY: 'auto', px: 1.5, pt: 1.5, pb: 0, position: 'relative' }}
        aria-live="polite"
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }} spacing={1.5}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading conversation…
            </Typography>
          </Stack>
        ) : effectiveMessages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Select a message from the inbox to see the full conversation thread here.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {effectiveMessages.map((message) => {
              const shouldObserve =
                markOnOpen && !message.is_read && meta && message.contact_id === meta.contact.id
              const isOwn = message.direction === 'out'

              return (
                <MessageBubble
                  key={message.id}
                  msg={message}
                  isOwn={isOwn}
                  registerVisibility={shouldObserve ? registerVisibility : undefined}
                  onToggleFlag={handleBubbleFlagToggle}
                  onToggleOnAir={handleBubbleOnAirToggle}
                />
              )
            })}
          </Stack>
        )}
      </Box>

      <Box className="ConversationPane__composer">
        <Divider />

        <Stack
          spacing={1}
          sx={{ p: 1.5 }}
          component="form"
          onSubmit={handleSubmit}
          className="rpa-thread-reply rpa-composer"
          ref={composerContainerRef}
        >
          <div className="rpa-actions-row">
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
              className="rpa-composer-actions"
            >
              <Tooltip title={targetMessage?.is_read ? 'Mark unread' : 'Mark read'}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (!targetMessage) {
                        return
                      }
                      if (
                        !ensureCallable(onMarkReadToggle, 'onMarkReadToggle', {
                          messageId: targetMessage.id,
                          next: !targetMessage.is_read,
                        })
                      ) {
                        return
                      }
                      void onMarkReadToggle(targetMessage, !targetMessage.is_read)
                    }}
                    disabled={!targetMessage || loading}
                    size="small"
                    aria-label={targetMessage?.is_read ? 'Mark unread' : 'Mark read'}
                  >
                    {targetMessage?.is_read ? <RadioButtonUncheckedIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={targetMessage?.is_flagged ? 'Unflag message' : 'Flag message'}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (!targetMessage) {
                        return
                      }
                      if (
                        !ensureCallable(onFlagToggle, 'onFlagToggle', {
                          messageId: targetMessage.id,
                          next: !targetMessage.is_flagged,
                        })
                      ) {
                        return
                      }
                      void onFlagToggle(targetMessage, !targetMessage.is_flagged)
                    }}
                    disabled={!targetMessage || loading}
                    size="small"
                    aria-label={targetMessage?.is_flagged ? 'Unflag message' : 'Flag message'}
                  >
                    {targetMessage?.is_flagged ? (
                      <FlagIcon fontSize="small" color="warning" />
                    ) : (
                      <OutlinedFlagIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={targetMessage?.is_on_air ? 'Remove on-air flag' : 'Mark for on-air'}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (!targetMessage) {
                        return
                      }
                      if (
                        !ensureCallable(onOnAirToggle, 'onOnAirToggle', {
                          messageId: targetMessage.id,
                          next: !targetMessage.is_on_air,
                        })
                      ) {
                        return
                      }
                      void onOnAirToggle(targetMessage, !targetMessage.is_on_air)
                    }}
                    disabled={!targetMessage || loading}
                    size="small"
                    aria-label={targetMessage?.is_on_air ? 'Remove on-air flag' : 'Mark for on-air'}
                  >
                    <RadioIcon fontSize="small" color={targetMessage?.is_on_air ? 'secondary' : 'inherit'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Block identity">
                <span>
                  <IconButton
                    onClick={onBlockIdentity}
                    disabled={!meta || loading || !meta?.active_identity}
                    size="small"
                    aria-label="Block identity"
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Block contact">
                <span>
                  <IconButton onClick={onBlockContact} disabled={!meta || loading} size="small" aria-label="Block contact">
                    <BlockIcon fontSize="small" color="warning" />
                  </IconButton>
                </span>
              </Tooltip>
              <QuickRepliesMenu onPick={handleQuickReply} disabled={isBlocked || disabled || loading} />
          </Stack>
          <div className="rpa-actions-spacer" />
          <Tooltip title="To bottom" placement="top">
            <span>
              <IconButton
                aria-label="Scroll to bottom"
                size="small"
                onClick={() => scrollToBottom('smooth')}
                disabled={isNearBottom}
                className="rpa-to-bottom-btn"
              >
                <ArrowDownwardRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems="flex-end">
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <TextField
              multiline
              minRows={1}
              maxRows={8}
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
              placeholder={isBlocked ? 'Replies are disabled for this contact' : 'Type your reply…'}
              fullWidth
              disabled={isBlocked || disabled || loading}
              inputRef={composerRef}
              onKeyDown={handleComposerKeyDown}
              onCompositionStart={handleComposerCompositionStart}
              onCompositionEnd={handleComposerCompositionEnd}
            />
            {enterToSend ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Enter to send • Shift+Enter for newline.
              </Typography>
            ) : null}
          </Box>
          <Button
            type="submit"
            variant="contained"
            endIcon={<SendIcon fontSize="small" />}
            disabled={isBlocked || disabled || loading || sending || sendingState || !replyText.trim()}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, minWidth: { xs: '100%', sm: 104 } }}
          >
            {sending || sendingState ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
        </Stack>
      </Box>
    </Paper>
  )
}

export default Conversation
